/**
 * Persists EV coverage manual edits via the Postgres coverage update API.
 *
 * @module coverageInfo/hooks/usePersistEvCoverageEdits
 */

import { useCallback } from "react";
import { useSelector } from "react-redux";
import { getSiblingOrderIdsByCoveragePlanDateRange } from "../../../context/utils/filterSiblingOrdersBasedOnConditions";
import type { SingleCoverageInformation } from "../../../data-model/coverageInformation";
import { SiblingOrder } from "../../../data-model/siblingOrdersModel";
import { CoverageStatus } from "../../../enums/coverageStatus";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { RootState } from "../../../redux/store/store";
import type { CoveragePostgresUpdateData } from "../helpers/coveragePostgresUpdateTypes";
import { mapCoverageInformationToPostgresUpdates } from "../helpers/mapCoverageInformationToPostgresUpdate";

/** Inputs for {@link usePersistEvCoverageEdits}. */
export interface UsePersistEvCoverageEditsArgs {
  orderId: string;
  orgId: string;
  evCoverageStatus: CoverageStatus;
  /** Postgres `coverage_type` for the active insurance tier tab. */
  coverageType: string;
  /** Called after a successful write so parents can refetch coverage. */
  onPersisted?: () => void;
  /** Raw sibling orders used to derive payload-scoped sibling fan-out ids. */
  siblingOrders?: SiblingOrder[];
}

/**
 * Exposes `persistCoverageEdit` to save a coverage UI snapshot to Postgres.
 *
 * @param args - Order context, EV status, and refetch callback
 * @returns Persist function (resolves to payload-scoped sibling order ids) and
 *   request loading flag
 */
export const usePersistEvCoverageEdits = (
  args: UsePersistEvCoverageEditsArgs,
) => {
  const {
    orderId,
    orgId,
    evCoverageStatus,
    coverageType,
    onPersisted,
    siblingOrders = [],
  } = args;
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const updatedBy = user?.email ?? "";

  const { runUpdate, loading } =
    useUpdateOrderDetails<CoveragePostgresUpdateData>({
      orderId,
      orgId,
      tableName: SingleOrderDetailsTableName.COVERAGE,
    });

  const persistCoverageEdit = useCallback(
    async (snapshot: SingleCoverageInformation): Promise<string[]> => {
      const payloads = mapCoverageInformationToPostgresUpdates(snapshot, {
        evCoverageStatus,
        coverageType,
        updatedBy,
      });
      if (payloads.length === 0) {
        throw new Error("No coverage fields to persist");
      }

      const siblingOrderIds = getSiblingOrderIdsByCoveragePlanDateRange(
        payloads[0]?.service_year_from,
        payloads[0]?.service_year_to,
        siblingOrders,
        { logStep: "[coverage-update]" },
      );

      for (const data of payloads) {
        const result = await runUpdate(data, siblingOrderIds);
        const scope = data.service_type
          ? `${data.coverage_type}/${data.service_type}`
          : data.coverage_type;

        if (!result.success || result.error) {
          throw (
            result.error ??
            new Error(`Coverage update request failed (${scope})`)
          );
        }
        if (!result.data?.success) {
          throw new Error(`Coverage update did not affect any rows (${scope})`);
        }
      }
      onPersisted?.();
      return siblingOrderIds;
    },
    [
      coverageType,
      evCoverageStatus,
      onPersisted,
      runUpdate,
      siblingOrders,
      updatedBy,
    ],
  );

  return { persistCoverageEdit, isPersisting: loading };
};
